import { Injectable } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ModalRefreshTokenService } from 'src/app/shared/services/modal-refresh-token.service';

/** Minutos antes del vencimiento absoluto para mostrar el modal de keep-alive. */
const REFRESH_WARNING_MS = 2 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  private sessionExpiresAt: number | null = null;
  private countdownSubscription?: Subscription;
  private hasShownRefreshWarning = false;
  private visibilityHandler?: () => void;

  constructor(
    private router: Router,
    private messageService: MessageService,
    private modalRefreshTokenService: ModalRefreshTokenService,
  ) {}

  /**
   * Inicia el ciclo de sesión con hora de vencimiento absoluta.
   * El tiempo se mide con Date.now(), no con un contador que se pause en background.
   */
  startCountdown(minutes: number) {
    this.stopCountdown();

    if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes <= 0) {
      return;
    }

    this.sessionExpiresAt = Date.now() + minutes * 60 * 1000;
    this.hasShownRefreshWarning = false;
    this.attachVisibilityListener();
    this.evaluateSessionState();

    this.countdownSubscription = interval(1000).subscribe(() => {
      this.evaluateSessionState();
    });
  }

  stopCountdown() {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = undefined;
    }

    this.detachVisibilityListener();
    this.sessionExpiresAt = null;
    this.hasShownRefreshWarning = false;
  }

  private evaluateSessionState() {
    if (this.sessionExpiresAt == null) {
      return;
    }

    const remainingMs = this.sessionExpiresAt - Date.now();

    if (remainingMs <= 0) {
      this.expireSession();
      return;
    }

    if (!this.hasShownRefreshWarning && remainingMs <= REFRESH_WARNING_MS) {
      this.hasShownRefreshWarning = true;
      this.modalRefreshTokenService.show();
    }
  }

  private expireSession() {
    this.modalRefreshTokenService.hide();
    this.stopCountdown();
    this.router.navigate(['/login'], { replaceUrl: true });
    this.messageService.add({
      severity: 'warn',
      summary: 'El tiempo de sesión expiró',
      detail: 'Inicie sesión nuevamente.',
    });
  }

  private attachVisibilityListener() {
    if (typeof document === 'undefined' || this.visibilityHandler) {
      return;
    }

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.evaluateSessionState();
      }
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private detachVisibilityListener() {
    if (typeof document === 'undefined' || !this.visibilityHandler) {
      return;
    }

    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.visibilityHandler = undefined;
  }
}
